import { useHistory } from '@fs/zion-router'
import React, { useMemo, useState } from 'react'
import { Autosuggest } from '@fs/zion-ui'
import { MenuSearch } from '@fs/zion-icon'
import { usePlaceReportsPageData } from '../../PlaceReportsProvider'
import { getLocaleName } from '../../placeReportsHelper'
import { type Region, type Country, type Division } from '../../types'

type SuggestionItem = {
  key: string
  primaryText: string
  secondaryText?: string
  tertiaryText?: string
  division?: Division
  country?: Country
  region?: Region
  path: string
}

function buildDivisionEntries(
  country: Country,
  region: Region,
  regionName: string,
  countryName: string
): SuggestionItem[] {
  return (country?.divisions ?? []).map((division) => {
    const divisionName = getLocaleName(division?.name, country?.native_lang)
    return {
      key: `division-${division.id}`,
      primaryText: `${divisionName}`,
      secondaryText: `${countryName}`,
      tertiaryText: `${regionName}`,
      division,
      country,
      region,
      path: `?region=${region.id}&country=${country.id}&division=${division.id}`,
    }
  })
}

function buildCountryEntries(region: Region, regionName: string): SuggestionItem[] {
  return (region?.countries ?? []).flatMap((country) => {
    const countryName = getLocaleName(country?.name, country?.native_lang)
    const countryEntry: SuggestionItem = {
      key: `country-${country.id}`,
      primaryText: `${countryName}`,
      secondaryText: `${regionName}`,
      country,
      region,
      path: `?region=${region.id}&country=${country.id}`,
    }
    return [countryEntry, ...buildDivisionEntries(country, region, regionName, countryName)]
  })
}

export default function ReportsSearch(): JSX.Element {
  const { reportsIndex } = usePlaceReportsPageData()
  const { regions } = reportsIndex || {}
  const history = useHistory()
  const [selectedItem, setSelectedItem] = useState<SuggestionItem | null>(null)

  const suggestions = useMemo(() => {
    if (!regions) return []

    return regions.flatMap((region) => {
      const regionName = getLocaleName(region?.name)
      const regionEntry: SuggestionItem = {
        key: `region-${region.id}`,
        primaryText: `${regionName}`,
        region,
        path: `?region=${region.id}`,
      }
      return [regionEntry, ...buildCountryEntries(region, regionName)]
    })
  }, [regions])

  return (
    <Autosuggest
      aria-label="Place Reports Search"
      highlightMatchingText
      selectedItem={selectedItem}
      Icon={MenuSearch}
      suggestions={suggestions}
      placeholder="Enter a place name"
      onSuggestionSelected={(suggestion) => {
        if (suggestion) {
          history.push(suggestion.path)
          setSelectedItem(null)
        }
      }}
      clearable
    />
  )
}
